/**
 * Mobile-first SVG line chart for the results view.
 *
 * Zero dependency by design: a canvas library would double the JS payload for
 * a page whose whole value is a two-line chart, and the product bar (see
 * AGENTS.md — "no third-party trackers") pushes against pulling in Chart.js,
 * Recharts, or D3 for what is ultimately a few paths and a couple of axis
 * labels. Everything renders into an SVG viewBox so the browser handles
 * scaling for us — the chart is crisp at any device pixel ratio and stays
 * within the mobile column at 375px viewport width.
 *
 * Chart shape:
 *  • Two lines — one per path (rent vs. own net worth).
 *  • A vertical marker at the break-even year (or a dimmed "no break-even"
 *    caption when owning never overtakes renting within the horizon).
 *  • Four y-axis grid lines with currency labels; year ticks every
 *    ceil(horizonYears/6) years so small horizons still get 3–6 ticks.
 *  • A single crosshair that follows pointer / touch to reveal the two path
 *    values at that year. Pointer events cover mouse, pen, and touch in
 *    modern browsers with one code path.
 *
 * The whole render is pure — take a spec, return an SVGSVGElement. The
 * results view owns the mount and simply replaces the SVG on state change.
 */

const SVG_NS = "http://www.w3.org/2000/svg";
const MONTHS_PER_YEAR = 12;

/** One month's value for the chart. Structural subset of BreakEvenMonthPoint. */
export interface ChartMonthPoint {
  readonly month: number;
  readonly rentPath: number;
  readonly ownPath: number;
}

export interface ChartSpec {
  readonly points: readonly ChartMonthPoint[];
  readonly horizonYears: number;
  /** Year Y in [1, horizonYears] where own overtakes rent, or null. */
  readonly breakEvenYear: number | null;
  /**
   * Compact currency formatter for axis labels (must fit in ~56px gutter —
   * e.g. "$2.6M"). Also used for the a11y summary.
   */
  readonly formatCurrencyCompact: (value: number) => string;
  /** Full-precision currency formatter for the crosshair tooltip. */
  readonly formatCurrency: (value: number) => string;
}

interface Extents {
  readonly minY: number;
  readonly maxY: number;
}

interface Layout {
  readonly width: number;
  readonly height: number;
  readonly plot: {
    readonly left: number;
    readonly right: number;
    readonly top: number;
    readonly bottom: number;
    readonly width: number;
    readonly height: number;
  };
}

/**
 * Viewbox dimensions. Held constant so the SVG scales via CSS width:100%;
 * the browser handles crispness. 360x240 keeps the aspect close to golden on
 * a phone and leaves room for a 44px axis gutter without cramping the plot.
 */
const LAYOUT: Layout = {
  width: 360,
  height: 240,
  plot: {
    left: 56,
    right: 12,
    top: 12,
    bottom: 32,
    get width() {
      return LAYOUT.width - this.left - this.right;
    },
    get height() {
      return LAYOUT.height - this.top - this.bottom;
    },
  },
};

/** Number of horizontal grid lines. Four gives 5 axis labels including 0. */
const Y_GRID_LINES = 4;

/**
 * Build the results-view chart. Returns a wrapper element that owns the SVG
 * plus a legend and an accessible fallback caption; results.ts appends it
 * verbatim into the results section.
 */
export function renderNetWorthChart(spec: ChartSpec): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "chart";

  const legend = document.createElement("div");
  legend.className = "chart-legend";
  legend.append(
    makeLegendItem("own", "Own — net worth if sold"),
    makeLegendItem("rent", "Rent — invested capital"),
  );
  wrapper.append(legend);

  const svg = buildSvg(spec);
  wrapper.append(svg);

  return wrapper;
}

/**
 * Legend entry with an inline-SVG swatch that mirrors the actual stroke
 * pattern of the series it represents (solid + circle for own, dashed +
 * diamond for rent). REN-16: readers who can't distinguish coral from navy
 * see two visibly different shapes.
 */
function makeLegendItem(series: "own" | "rent", label: string): HTMLElement {
  const item = document.createElement("span");
  item.className = `legend-item legend-${series}`;
  const swatch = document.createElementNS(SVG_NS, "svg");
  swatch.setAttribute("viewBox", "0 0 26 12");
  swatch.setAttribute("class", "swatch-svg");
  swatch.setAttribute("aria-hidden", "true");
  const line = document.createElementNS(SVG_NS, "line");
  line.setAttribute("x1", "1");
  line.setAttribute("x2", "25");
  line.setAttribute("y1", "6");
  line.setAttribute("y2", "6");
  line.setAttribute("stroke", "currentColor");
  line.setAttribute("stroke-width", "2");
  line.setAttribute("stroke-linecap", "round");
  if (series === "rent") line.setAttribute("stroke-dasharray", "6 4");
  swatch.append(line);
  swatch.append(makeSwatchMarker(series));
  item.append(swatch);
  item.append(document.createTextNode(label));
  return item;
}

function makeSwatchMarker(series: "own" | "rent"): SVGElement {
  if (series === "own") {
    const dot = document.createElementNS(SVG_NS, "circle");
    dot.setAttribute("cx", "13");
    dot.setAttribute("cy", "6");
    dot.setAttribute("r", "3");
    dot.setAttribute("fill", "currentColor");
    return dot;
  }
  const diamond = document.createElementNS(SVG_NS, "polygon");
  diamond.setAttribute("points", "13,2 17,6 13,10 9,6");
  diamond.setAttribute("fill", "currentColor");
  return diamond;
}

function buildSvg(spec: ChartSpec): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", `0 0 ${LAYOUT.width} ${LAYOUT.height}`);
  svg.setAttribute("role", "img");
  const description = describeChart(spec);
  svg.setAttribute("aria-label", description);
  // A native <title> child is picked up by every screen reader; the visible
  // aria-label is redundant safety for readers that ignore <title> on inline
  // SVG.
  const title = document.createElementNS(SVG_NS, "title");
  title.textContent = description;
  svg.append(title);
  svg.classList.add("chart-svg");

  const extents = computeExtents(spec.points);
  const xScale = makeXScale(spec.horizonYears);
  const yScale = makeYScale(extents);

  drawGrid(svg, extents, yScale, spec.formatCurrencyCompact);
  drawXAxis(svg, spec.horizonYears, xScale);

  if (spec.breakEvenYear !== null) {
    drawBreakEvenMarker(svg, spec.breakEvenYear, xScale);
  }

  const rentPath = pathFor(spec.points, "rentPath", xScale, yScale);
  const ownPath = pathFor(spec.points, "ownPath", xScale, yScale);
  svg.append(makePath(rentPath, "series series-rent"));
  svg.append(makePath(ownPath, "series series-own"));

  attachCrosshair(svg, spec, xScale, yScale);

  return svg;
}

/**
 * Y-axis extents include zero so the reader can see how far each portfolio
 * has grown from its starting capital, and are padded by 8% at the top to
 * keep the last data label from touching the plot border.
 */
function computeExtents(points: readonly ChartMonthPoint[]): Extents {
  let minY = 0;
  let maxY = 0;
  for (const point of points) {
    if (point.rentPath < minY) minY = point.rentPath;
    if (point.ownPath < minY) minY = point.ownPath;
    if (point.rentPath > maxY) maxY = point.rentPath;
    if (point.ownPath > maxY) maxY = point.ownPath;
  }
  if (maxY === minY) {
    maxY = minY + 1;
  }
  const pad = (maxY - minY) * 0.08;
  return { minY, maxY: maxY + pad };
}

function makeXScale(horizonYears: number): (month: number) => number {
  const totalMonths = horizonYears * MONTHS_PER_YEAR;
  const { left, width } = LAYOUT.plot;
  return (month) => left + (month / totalMonths) * width;
}

function makeYScale(extents: Extents): (value: number) => number {
  const { top, height } = LAYOUT.plot;
  const span = extents.maxY - extents.minY;
  return (value) => top + height - ((value - extents.minY) / span) * height;
}

function pathFor(
  points: readonly ChartMonthPoint[],
  key: "rentPath" | "ownPath",
  xScale: (month: number) => number,
  yScale: (value: number) => number,
): string {
  if (points.length === 0) return "";
  const parts: string[] = [];
  // Anchor at month 0 = initial capital state (starting portfolio value).
  const first = points[0];
  if (!first) return "";
  parts.push(`M ${xScale(0).toFixed(2)} ${yScale(first[key]).toFixed(2)}`);
  for (const point of points) {
    parts.push(`L ${xScale(point.month).toFixed(2)} ${yScale(point[key]).toFixed(2)}`);
  }
  return parts.join(" ");
}

function makePath(d: string, className: string): SVGPathElement {
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("d", d);
  path.setAttribute("fill", "none");
  path.setAttribute("class", className);
  return path;
}

function drawGrid(
  svg: SVGSVGElement,
  extents: Extents,
  yScale: (value: number) => number,
  formatCurrency: (value: number) => string,
): void {
  const group = document.createElementNS(SVG_NS, "g");
  group.setAttribute("class", "chart-grid");
  const span = extents.maxY - extents.minY;
  for (let i = 0; i <= Y_GRID_LINES; i++) {
    const value = extents.minY + (span * i) / Y_GRID_LINES;
    const y = yScale(value);
    const line = document.createElementNS(SVG_NS, "line");
    line.setAttribute("x1", String(LAYOUT.plot.left));
    line.setAttribute("x2", String(LAYOUT.width - LAYOUT.plot.right));
    line.setAttribute("y1", y.toFixed(2));
    line.setAttribute("y2", y.toFixed(2));
    group.append(line);

    const label = document.createElementNS(SVG_NS, "text");
    label.setAttribute("x", String(LAYOUT.plot.left - 6));
    label.setAttribute("y", (y + 3).toFixed(2));
    label.setAttribute("text-anchor", "end");
    label.setAttribute("class", "chart-axis-label");
    label.textContent = formatCurrency(value);
    group.append(label);
  }
  svg.append(group);
}

function drawXAxis(
  svg: SVGSVGElement,
  horizonYears: number,
  xScale: (month: number) => number,
): void {
  const group = document.createElementNS(SVG_NS, "g");
  group.setAttribute("class", "chart-axis-x");
  const step = Math.max(1, Math.ceil(horizonYears / 6));
  const baselineY = LAYOUT.height - LAYOUT.plot.bottom;
  for (let year = 0; year <= horizonYears; year += step) {
    const x = xScale(year * MONTHS_PER_YEAR);
    const label = document.createElementNS(SVG_NS, "text");
    label.setAttribute("x", x.toFixed(2));
    label.setAttribute("y", String(baselineY + 18));
    label.setAttribute("text-anchor", "middle");
    label.setAttribute("class", "chart-axis-label");
    label.textContent = year === 0 ? "now" : `${year}y`;
    group.append(label);
  }
  svg.append(group);
}

function drawBreakEvenMarker(
  svg: SVGSVGElement,
  breakEvenYear: number,
  xScale: (month: number) => number,
): void {
  const x = xScale(breakEvenYear * MONTHS_PER_YEAR);
  const marker = document.createElementNS(SVG_NS, "line");
  marker.setAttribute("class", "chart-breakeven");
  marker.setAttribute("x1", x.toFixed(2));
  marker.setAttribute("x2", x.toFixed(2));
  marker.setAttribute("y1", String(LAYOUT.plot.top));
  marker.setAttribute("y2", String(LAYOUT.height - LAYOUT.plot.bottom));
  svg.append(marker);

  const label = document.createElementNS(SVG_NS, "text");
  label.setAttribute("class", "chart-breakeven-label");
  label.setAttribute("x", (x + 4).toFixed(2));
  label.setAttribute("y", String(LAYOUT.plot.top + 12));
  label.textContent = `break-even ${breakEvenYear}y`;
  svg.append(label);
}

function describeChart(spec: ChartSpec): string {
  const last = spec.points[spec.points.length - 1];
  if (!last) return "Rent vs. own net-worth comparison chart.";
  const be = spec.breakEvenYear !== null ? `year ${spec.breakEvenYear}` : "never within horizon";
  return `Rent vs. own net-worth over ${spec.horizonYears} years. Break-even at ${be}. Final rent ${spec.formatCurrencyCompact(last.rentPath)}, own ${spec.formatCurrencyCompact(last.ownPath)}.`;
}

interface XScaleFn {
  (month: number): number;
}
interface YScaleFn {
  (value: number): number;
}

function attachCrosshair(
  svg: SVGSVGElement,
  spec: ChartSpec,
  xScale: XScaleFn,
  yScale: YScaleFn,
): void {
  const totalMonths = spec.horizonYears * MONTHS_PER_YEAR;

  const line = document.createElementNS(SVG_NS, "line");
  line.setAttribute("class", "chart-crosshair");
  line.setAttribute("y1", String(LAYOUT.plot.top));
  line.setAttribute("y2", String(LAYOUT.height - LAYOUT.plot.bottom));
  line.setAttribute("x1", "0");
  line.setAttribute("x2", "0");
  line.style.opacity = "0";
  svg.append(line);

  // Distinct shapes reinforce the color signal so the two series stay
  // distinguishable to readers who can't distinguish coral from navy
  // (REN-16).
  const ownDot = makeCircleMarker("chart-dot chart-dot-own");
  const rentDot = makeDiamondMarker("chart-dot chart-dot-rent");
  svg.append(rentDot, ownDot);

  const tooltip = document.createElementNS(SVG_NS, "g");
  tooltip.setAttribute("class", "chart-tooltip");
  tooltip.style.opacity = "0";
  const bg = document.createElementNS(SVG_NS, "rect");
  bg.setAttribute("rx", "6");
  bg.setAttribute("ry", "6");
  bg.setAttribute("class", "chart-tooltip-bg");
  const yearText = document.createElementNS(SVG_NS, "text");
  yearText.setAttribute("class", "chart-tooltip-title");
  const ownText = document.createElementNS(SVG_NS, "text");
  ownText.setAttribute("class", "chart-tooltip-line chart-tooltip-own");
  const rentText = document.createElementNS(SVG_NS, "text");
  rentText.setAttribute("class", "chart-tooltip-line chart-tooltip-rent");
  tooltip.append(bg, yearText, ownText, rentText);
  svg.append(tooltip);

  // Transparent hit-target the size of the plot area — captures pointer
  // events across the full chart even where the drawn lines aren't.
  const hit = document.createElementNS(SVG_NS, "rect");
  hit.setAttribute("x", String(LAYOUT.plot.left));
  hit.setAttribute("y", String(LAYOUT.plot.top));
  hit.setAttribute("width", String(LAYOUT.plot.width));
  hit.setAttribute("height", String(LAYOUT.plot.height));
  hit.setAttribute("fill", "transparent");
  hit.style.cursor = "crosshair";
  svg.append(hit);

  const hide = () => {
    line.style.opacity = "0";
    rentDot.style.opacity = "0";
    ownDot.style.opacity = "0";
    tooltip.style.opacity = "0";
  };

  const show = (clientX: number) => {
    const rect = svg.getBoundingClientRect();
    if (rect.width === 0) return;
    const svgX = ((clientX - rect.left) / rect.width) * LAYOUT.width;
    const clampedX = Math.max(LAYOUT.plot.left, Math.min(svgX, LAYOUT.width - LAYOUT.plot.right));
    const frac = (clampedX - LAYOUT.plot.left) / LAYOUT.plot.width;
    const monthGuess = Math.round(frac * totalMonths);
    const monthIdx = Math.max(1, Math.min(monthGuess, spec.points.length));
    const point = spec.points[monthIdx - 1];
    if (!point) return;

    const px = xScale(point.month);
    const rentY = yScale(point.rentPath);
    const ownY = yScale(point.ownPath);

    line.setAttribute("x1", px.toFixed(2));
    line.setAttribute("x2", px.toFixed(2));
    line.style.opacity = "1";

    positionDiamond(rentDot, px, rentY);
    rentDot.style.opacity = "1";

    ownDot.setAttribute("cx", px.toFixed(2));
    ownDot.setAttribute("cy", ownY.toFixed(2));
    ownDot.style.opacity = "1";

    const year = point.month / MONTHS_PER_YEAR;
    const yearLabel = Number.isInteger(year) ? `Year ${year}` : `Year ${year.toFixed(1)}`;
    yearText.textContent = yearLabel;
    // Prefix the tooltip series labels with the same shapes used on the
    // legend and crosshair so a monochrome reader can still tell which
    // number is which (REN-16).
    ownText.textContent = `● Own ${spec.formatCurrency(point.ownPath)}`;
    rentText.textContent = `◆ Rent ${spec.formatCurrency(point.rentPath)}`;

    // Tooltip box positioning — flip to the left of the crosshair when we
    // would otherwise clip the right edge of the plot.
    const boxWidth = 128;
    const boxHeight = 60;
    const preferLeft = px + boxWidth + 12 > LAYOUT.width - LAYOUT.plot.right;
    const boxX = preferLeft ? px - boxWidth - 8 : px + 8;
    const boxY = Math.min(
      Math.max(LAYOUT.plot.top, Math.min(rentY, ownY) - boxHeight - 6),
      LAYOUT.height - LAYOUT.plot.bottom - boxHeight,
    );
    bg.setAttribute("x", String(boxX));
    bg.setAttribute("y", String(boxY));
    bg.setAttribute("width", String(boxWidth));
    bg.setAttribute("height", String(boxHeight));
    yearText.setAttribute("x", String(boxX + 10));
    yearText.setAttribute("y", String(boxY + 16));
    ownText.setAttribute("x", String(boxX + 10));
    ownText.setAttribute("y", String(boxY + 34));
    rentText.setAttribute("x", String(boxX + 10));
    rentText.setAttribute("y", String(boxY + 50));
    tooltip.style.opacity = "1";
  };

  hit.addEventListener("pointermove", (event) => show(event.clientX));
  hit.addEventListener("pointerdown", (event) => show(event.clientX));
  hit.addEventListener("pointerleave", hide);
}

function makeCircleMarker(className: string): SVGCircleElement {
  const dot = document.createElementNS(SVG_NS, "circle");
  dot.setAttribute("class", className);
  dot.setAttribute("r", "4");
  dot.style.opacity = "0";
  return dot;
}

function makeDiamondMarker(className: string): SVGPolygonElement {
  const diamond = document.createElementNS(SVG_NS, "polygon");
  diamond.setAttribute("class", className);
  diamond.style.opacity = "0";
  return diamond;
}

/**
 * A diamond marker (rotated square) — 5px half-diagonal so its visual weight
 * roughly matches the r=4 circle used by the "own" series.
 */
function positionDiamond(node: SVGPolygonElement, cx: number, cy: number): void {
  const half = 5;
  const points = [
    `${cx.toFixed(2)},${(cy - half).toFixed(2)}`,
    `${(cx + half).toFixed(2)},${cy.toFixed(2)}`,
    `${cx.toFixed(2)},${(cy + half).toFixed(2)}`,
    `${(cx - half).toFixed(2)},${cy.toFixed(2)}`,
  ].join(" ");
  node.setAttribute("points", points);
}

/**
 * Exported for tests: pure scale + extent helpers so we can verify the
 * axis math without needing a DOM. Not part of the public rendering API.
 */
export const __chartInternals = {
  LAYOUT,
  computeExtents,
  makeXScale,
  makeYScale,
  pathFor,
};
